import * as React from 'react';
import { Phone, MessageCircle } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { StatusBadge } from './status-badge';
import { cn } from '../../lib/utils';

export interface EmployeeCardProps {
  name: string;
  designation?: string;
  department?: string;
  avatar?: string;
  email?: string;
  phone?: string;
  status?: 'active' | 'on-leave' | 'weekend' | 'absent';
  onCall?: () => void;
  onMessage?: () => void;
  onClick?: () => void;
  className?: string;
}

// Generate initials from name
function getInitials(name: string): string {
  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

// Get color for avatar background based on initials
function getAvatarColor(name: string): string {
  const colors = [
    'bg-[var(--color-primary)]',
    'bg-[var(--color-success)]',
    'bg-[var(--color-warning)]',
    'bg-[var(--color-destructive)]',
    'bg-[var(--color-secondary)]',
  ];
  const hash = name.charCodeAt(0) + name.charCodeAt(name.length - 1);
  return colors[hash % colors.length];
}

// Get status indicator color
function getStatusIndicatorColor(status?: string): string {
  if (!status) return 'bg-[var(--color-muted)]';

  const statusColorMap: Record<string, string> = {
    active: 'bg-[var(--color-success)]',
    'on-leave': 'bg-[var(--color-warning)]',
    weekend: 'bg-[var(--color-secondary)]',
    absent: 'bg-[var(--color-destructive)]',
  };

  return statusColorMap[status] || 'bg-[var(--color-muted)]';
}

export const EmployeeCard = React.forwardRef<HTMLDivElement, EmployeeCardProps>(
  (
    {
      name,
      designation,
      department,
      avatar,
      email,
      phone,
      status,
      onCall,
      onMessage,
      onClick,
      className,
    },
    ref,
  ) => {
    const isClickable = !!onClick;
    const initials = getInitials(name);
    const avatarColor = getAvatarColor(name);
    const statusColor = getStatusIndicatorColor(status);

    return (
      <Card
        ref={ref}
        onClick={onClick}
        className={cn(
          'flex items-center justify-between p-4',
          isClickable && 'cursor-pointer transition-shadow hover:shadow-md',
          className,
        )}
      >
        <CardContent className="flex items-center gap-3 p-0">
          {/* Avatar with status indicator */}
          <div className="relative flex-shrink-0">
            {avatar ? (
              <img
                src={avatar}
                alt={name}
                className="h-12 w-12 rounded-full object-cover"
              />
            ) : (
              <div
                className={cn(
                  'flex h-12 w-12 items-center justify-center rounded-full font-semibold text-white',
                  avatarColor,
                )}
              >
                {initials}
              </div>
            )}
            {/* Status indicator dot */}
            {status && (
              <div
                className={cn(
                  'absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-[var(--color-card)]',
                  statusColor,
                )}
              />
            )}
          </div>

          {/* Employee info */}
          <div className="flex-1">
            <p className="font-semibold text-[var(--color-foreground)]">{name}</p>
            {designation && (
              <p className="text-xs text-[var(--color-muted-foreground)]">
                {designation}
              </p>
            )}
            {department && (
              <p className="text-xs text-[var(--color-muted-foreground)]">
                {department}
              </p>
            )}
          </div>
        </CardContent>

        {/* Status badge and actions */}
        <div className="flex items-center gap-2">
          {status && <StatusBadge status={status} variant="pill" size="sm" />}

          <div className="flex gap-1">
            {onCall && (
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                  onCall();
                }}
                className="h-8 w-8"
                title="Call"
              >
                <Phone className="h-4 w-4" />
              </Button>
            )}
            {onMessage && (
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                  onMessage();
                }}
                className="h-8 w-8"
                title="Message"
              >
                <MessageCircle className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </Card>
    );
  },
);

EmployeeCard.displayName = 'EmployeeCard';
